import './TopUp.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import TonWeb from 'tonweb';
import copyToClipboard from 'copy-text-to-clipboard';
import { useDispatch, useSelector } from 'react-redux';
import vkQr from '@vkontakte/vk-qr';

import { Button, Form, FormItem, Header, Placeholder, ScrollView } from '$uikit';
import { useCurrentUserInfo, useTimer, useTranslator } from '$hooks';
import { IcChevron16, IcCopy28 } from '$assets';
import { toastActions } from '$store/toast';
import { mainSelector } from '$store/main';
import { authSelector } from '$store/auth';
import { AuthRequired } from '$core/AuthRequired/AuthRequired';
import { balanceActions, balanceSelector } from '$store/balance';
import { Loader } from '$shared/components';
import { rnbr } from '$utils';
import { useTime } from 'react-timer-hook';

export const TopUp: FC = () => {
  const dispatch = useDispatch();
  const user = useCurrentUserInfo();
  const t = useTranslator();
  const { topUpAddress } = useSelector(mainSelector);
  const { isLogged } = useSelector(authSelector);
  const [method, setMethod] = useState('none');
  const { isLoading, isFailed, bscAddress } = useSelector(balanceSelector);
  const timer = useTime();

  useEffect(() => {
    dispatch(balanceActions.loadBalance());
  }, []);

  useEffect(() => {
    if (method === 'native' && !isLoading) {
      dispatch(balanceActions.startCheckTonBalanceTopUp());
    }
    return () => {
      dispatch(balanceActions.stopCheckTonBalanceTopUp());
    };
  }, [method, isLoading]);

  const hexID = useMemo(() => {
    return new Number(user.id).toString(16);
  }, []);

  const handleCopyAddress = useCallback(() => {
    copyToClipboard(topUpAddress);
    dispatch(toastActions.setToastSuccess(t('copied')));
  }, [dispatch, topUpAddress]);

  const handleCopyComment = useCallback(() => {
    copyToClipboard(hexID);
    dispatch(toastActions.setToastSuccess(t('copied')));
  }, [dispatch, hexID]);

  const href = useMemo(() => {
    return `ton://transfer/${topUpAddress}?amount=${TonWeb.utils.toNano(
      1,
    )}&text=${hexID}`;
  }, [hexID, topUpAddress]);

  const qrCode = useMemo(() => {
    return vkQr.createQR(href, {
      qrSize: 200,
      isShowLogo: false,
      isShowBackground: true,
    });
  }, [href]);

  const handleMethod = useCallback(
    (newMethod) => () => {
      setMethod(newMethod);
    },
    [],
  );

  const handleRetry = useCallback(() => {
    dispatch(balanceActions.loadBalance());
  }, [dispatch]);

  const handleGetAddress = useCallback(() => {
    dispatch(balanceActions.getAddress('bsc'));
  }, [dispatch]);

  const handleCopy = useCallback(() => {
    copyToClipboard(bscAddress!.address);
    dispatch(toastActions.setToastSuccess(t('copied')));
  }, [dispatch, bscAddress]);

  function renderBscAddress() {
    if (isLoading) {
      return (
        <Placeholder>
          <Loader />
        </Placeholder>
      );
    }

    if (isFailed) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={<Button onClick={handleRetry}>{t('failed_button')}</Button>}
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    if (bscAddress && bscAddress.expireAt > Date.now() / 1000) {
      return (
        <Form>
          <FormItem
            title={t('top_up_swap_tmp_address_title')}
            description={
              <span className="TopUp__address__warn">
                {t('top_up_swap_tmp_address_caption')}
              </span>
            }
          >
            <div className="TopUp__address__wrap" onClick={handleCopy}>
              <div className="TopUp__address">{bscAddress.address}</div>
              <IcCopy28 />
            </div>
            <div className="TopUp__timer">
              {t('top_up_swap_tmp_address_timer')} {timer}
            </div>
          </FormItem>
          <FormItem>
            <Button
              onClick={handleMethod('none')}
              mode="secondary"
              style={{ marginTop: 16 }}
            >
              {t('back')}
            </Button>
          </FormItem>
        </Form>
      );
    }

    return (
      <div className="TopUp__pancake_info">
        <div className="TopUp__pancake_info__title">
          {t('top_up_swap_tmp_address_info_title')}
        </div>
        {t('top_up_swap_tmp_address_info')}
        <Button onClick={handleGetAddress}>
          {t('top_up_swap_tmp_address_get_button')}
        </Button>
        <Button onClick={handleMethod('none')} mode="secondary" style={{ marginTop: 16 }}>
          {t('back')}
        </Button>
      </div>
    );
  }

  function renderContent() {
    if (method === 'native') {
      return (
        <div className="TopUp__cont">
          <div className="TopUp__info_qr">
            <div dangerouslySetInnerHTML={{ __html: qrCode }} />
          </div>
          <div className="TopUp__info_row">
            <div className="TopUp__info_row__label">{t('top_up_comment_label')}</div>
            <div className="TopUp__info_row__value" onClick={handleCopyComment}>
              {hexID}
              <div className="TopUp__info_row__copy">
                <IcCopy28 />
              </div>
            </div>
            <div className="TopUp__info_comment_tip">{rnbr(t('top_up_comment_tip'))}</div>
          </div>
          <div className="TopUp__info_row">
            <div className="TopUp__info_row__label">{t('top_up_address_label')}</div>
            <div className="TopUp__info_row__value" onClick={handleCopyAddress}>
              {topUpAddress}
              <div className="TopUp__info_row__copy">
                <IcCopy28 />
              </div>
            </div>
          </div>
          <div className="TopUp__button">
            <Button href={href} target="_blank">
              {t('top_up_send')}
            </Button>
            <Button onClick={handleMethod('none')} mode="secondary">
              {t('back')}
            </Button>
          </div>
        </div>
      );
    } else if (method === 'buy') {
      return (
        <>
          <a
            className="TopUp__item"
            href="https://exchange.mercuryo.io/?currency=TONCOIN"
            target="_blank"
            rel="noreferrer"
          >
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">Mercuryo</div>
              <div className="TopUp__item__caption">{t('top_up_swap_buy_mercuryo')}</div>
            </div>
            <IcChevron16 />
          </a>
          <a
            className="TopUp__item"
            href="https://neocrypto.net/"
            target="_blank"
            rel="noreferrer"
          >
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">NeoCrypto</div>
              <div className="TopUp__item__caption">{t('top_up_swap_buy_neocrypto')}</div>
            </div>
            <IcChevron16 />
          </a>
          <a
            className="TopUp__item"
            href="https://t.me/wallet"
            target="_blank"
            rel="noreferrer"
          >
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">WalletBot</div>
              <div className="TopUp__item__caption">{t('top_up_swap_buy_walletbot')}</div>
            </div>
            <IcChevron16 />
          </a>
          <a
            className="TopUp__item"
            href="https://t.me/CryptoBot?start=r-1325-market"
            target="_blank"
            rel="noreferrer"
          >
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">CryptoBot</div>
              <div className="TopUp__item__caption">{t('top_up_swap_buy_cryptobot')}</div>
            </div>
            <IcChevron16 />
          </a>
          <div style={{ padding: 16 }}>
            <Button onClick={handleMethod('none')} mode="secondary">
              {t('back')}
            </Button>
          </div>
        </>
      );
    } else if (method === 'swap') {
      return (
        <>
          <div className="TopUp__pancake_info">
            {t('top_up_swap_info')}
            <Button
              href="https://pancakeswap.finance/swap?outputCurrency=0x76A797A59Ba2C17726896976B7B3747BfD1d220f"
              target="_blank"
            >
              {t('top_up_swap_button')}
            </Button>
          </div>
          <div className="TopUp__divider_wrap">
            <div className="TopUp__divider" />
          </div>
          {renderBscAddress()}
        </>
      );
    } else {
      return (
        <>
          <div className="TopUp__item" onClick={handleMethod('native')}>
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">{t('top_up_native_title')}</div>
              <div className="TopUp__item__caption">{t('top_up_native_caption')}</div>
            </div>
            <IcChevron16 />
          </div>
          <div className="TopUp__item" onClick={handleMethod('buy')}>
            <div className="TopUp__item__cont">
              <div className="TopUp__item__title">{t('top_up_buy_title')}</div>
              <div className="TopUp__item__caption">{t('top_up_buy_caption')}</div>
            </div>
            <IcChevron16 />
          </div>
        </>
      );
    }
  }

  if (!isLogged) {
    return <AuthRequired />;
  }

  return (
    <>
      <Header showCloseButton>{t('top_up_title')}</Header>
      <ScrollView>{renderContent()}</ScrollView>
    </>
  );
};
